import React, { FC, useCallback, useMemo } from "react"
import { useField, useFormikContext } from "formik"
import { useTranslation } from "next-i18next"
import { assertNever } from "@aistihealth/web-common/src/typings/typeUtils"
import styled from "styled-components"
import { theme } from "../../util/styles/theme"
import { DatePicker, DatePickerProps } from "../date-picker/DatePicker"
import { InputLabel } from "../input-label/InputLabel"
import { InputProps, InputSize } from "../inputs/base-input/BaseInput"
import { TextInput } from "../inputs/text-input/TextInput"
import {
  SelectInput,
  SelectInputProps,
} from "../inputs/select-input/SelectInput"
import { TextareaInput } from "../inputs/textarea-input/TextareaInput"
import { Tooltip } from "../tooltip/Tooltip"
import { FormInputDescriptionText } from "./FormInputDescriptionText"
import { ValidationErrorMessage } from "./ValidationErrorMessage"
import {
  isTranslatedValidationError,
  TranslatedValidationError,
} from "../../util/typing/yup"

const FormInputContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacings.small};
`

interface FormInputBaseProps {
  className?: string
  description?: string
  disabled?: boolean
  displayName?: string
  errorsAsTooltip?: boolean
  formId: string
  name: string
  placeholder?: string
  required?: boolean
  size?: InputSize
}

export type FormInputSelectProps = FormInputBaseProps &
  Pick<SelectInputProps, "options" | "showEmptyValueOption"> & {
    type: "select"
  }

export type FormInputDatePickerProps = FormInputBaseProps &
  Pick<DatePickerProps, "format"> & {
    type: "datepicker"
  }

export interface FormInputOthersProps extends FormInputBaseProps {
  type: "text" | "textarea"
}

export type FormInputProps =
  | FormInputSelectProps
  | FormInputDatePickerProps
  | FormInputOthersProps

// TODO #AIS-531 Clarify (get rid of) this wrapper. Needed to be able to use DatePicker
// in formik, but this is a bit messy. Should check if DatePicker could be used
// as other input components are used (TextInput etc.)
type DatePickerInputWrapperProps = Pick<FormInputDatePickerProps, "name"> &
  InputProps &
  Pick<DatePickerProps, "format" | "placeholder">

const DatePickerInputWrapper: FC<DatePickerInputWrapperProps> = ({
  error,

  ...props
}) => {
  const { setFieldValue } = useFormikContext()
  const [field] = useField(props)

  const handleChange = useCallback(
    dateTime => {
      setFieldValue(field.name, dateTime?.toJSDate())
    },
    [field.name, setFieldValue],
  )

  return <DatePicker error={error} {...props} onChange={handleChange} />
}

export const FormInput: FC<FormInputProps> = props => {
  const {
    className,
    disabled,
    displayName,
    description,
    formId,
    name,
    required,
    placeholder,
    size,
    errorsAsTooltip,
    ...rest
  } = props
  const [field, meta] = useField(props)
  const { t } = useTranslation()

  const [Component, additionalProps] = useMemo(() => {
    switch (rest.type) {
      case "text":
        return [TextInput, {}]
      case "textarea":
        return [TextareaInput, {}]
      case "select":
        return [
          SelectInput,
          {
            options: rest.options,
            showEmptyValueOption: rest.showEmptyValueOption,
          },
        ]
      case "datepicker":
        return [
          DatePickerInputWrapper,
          {
            format: rest.format,
          },
        ]
      default:
        return assertNever(rest)
    }
  }, [rest])

  const inputId = `${formId}-${name}`
  const validationFailed = Boolean(meta.touched && meta.error)

  const translatedError: TranslatedValidationError = isTranslatedValidationError(
    meta.error,
  )
    ? meta.error
    : { interpolationVariables: {}, key: meta.error } // Fallback: Some errors can be just strings (either custom or yup's default stuff)

  const showToolTipError = validationFailed && errorsAsTooltip
  const toolTipId = showToolTipError && `tooltip-${inputId}`
  return (
    <>
      <FormInputContainer className={className}>
        {displayName && (
          <InputLabel htmlFor={inputId}>
            {displayName}
            {required ? " *" : ""}
          </InputLabel>
        )}
        <Component
          data-tip={showToolTipError}
          data-for={toolTipId}
          id={inputId}
          error={validationFailed}
          placeholder={placeholder || displayName}
          disabled={disabled}
          required={required}
          aria-invalid={validationFailed}
          componentSize={size}
          {...additionalProps}
          {...field}
        />
        {description && (
          <FormInputDescriptionText>{description}</FormInputDescriptionText>
        )}
        {validationFailed && !errorsAsTooltip && (
          <ValidationErrorMessage htmlFor={inputId}>
            {t(translatedError.key, translatedError.interpolationVariables)}
          </ValidationErrorMessage>
        )}
      </FormInputContainer>
      {showToolTipError && (
        <Tooltip id={toolTipId} variant="error">
          {t(translatedError.key, translatedError.interpolationVariables)}
        </Tooltip>
      )}
    </>
  )
}
FormInput.defaultProps = {
  required: false,
  size: "medium",
}
